import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {noop} from '@Utils/general.util';
import {IconSize} from '@Components/icon-v2/icon.types';
import {InputField} from '@Components/input-field';
import {Pill, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {filterColorPalettesBySearchTerm} from '@Libraries/auto-branding-email-maker';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {getUniqueString} from '@Utils/string.util';
import styles from './color-palette-search-sector.module.scss';
import type {ColorPaletteInfo, ColorPalettePill} from '@Libraries/color-palettes.library';
import {getAdminColorPalettesFromRedux, getColorPaletteFilterPills} from '@Libraries/color-palettes.library';
import {WavyText} from '@Components/wavy-text/wavy-text';
import {ColorType} from '@Components/wavy-text/wavy-text.types';
import {TextSize} from '@Components/text';
import {SeasonalDecorationEmoji} from '@/utils/seasonal-promotion.util';
import {SeasonName} from '@/libraries/seasons.library';

interface ColorPaletteSearchSectorProps {
  onSearchSubmit?: (searchTerm: string, colorPalettes: ColorPaletteInfo[]) => void;
  onSearchInput?: (searchTerm: string) => void;
  onSelectedPillsChange?: (selectedPills: ColorPalettePill[], newlyToggledPill?: ColorPalettePill) => void;
  showSearchResults?: boolean;
  showShadow?: boolean;
  brandKitPalettes?: ColorPaletteInfo[];
}

enum DIRECTION {
  LEFT = 1,
  RIGHT = -1,
}

const PILL_SCROLL_WIDTH = 100;

type CustomPillTextContentProps = typeof WavyText;
type CustomPillTextContentType = ReactElement<CustomPillTextContentProps, typeof WavyText>;

export function ColorPaletteSearchSector({
  onSearchSubmit = noop,
  onSearchInput = noop,
  onSelectedPillsChange = noop,
  showSearchResults = false,
  showShadow = false,
  brandKitPalettes = [],
}: ColorPaletteSearchSectorProps): ReactElement<any> {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedPills, setSelectedPills] = useState<ColorPalettePill[]>([]);
  const [showPillScrollLeft, setShowPillScrollLeft] = useState<boolean>(false);
  const [showPillScrollRight, setShowPillScrollRight] = useState<boolean>(false);
  const [isInputActive, setIsInputActive] = useState<boolean>(false);
  const pillsContainerRef = useRef<HTMLDivElement>(null);
  const [colorPalettePills, setColorPalettePills] = useState<ColorPalettePill[]>([]);
  const [colorPalettePillsLoaded, setColorPalettePillsLoaded] = useState<boolean>(false);

  const getSearchFieldIcon = (): string => {
    return searchTerm ? 'icon-close' : 'icon-search';
  };

  const showPillScrollIfNeeded = (): void => {
    const pillsContainer = pillsContainerRef.current;

    if (!pillsContainer) {
      return;
    }

    const {scrollLeft, scrollWidth, clientWidth} = pillsContainer;
    const scrollRight = scrollWidth - scrollLeft - clientWidth;

    const scrollLeftShown = scrollLeft >= 1;
    const scrollRightShown = scrollRight >= 1;

    setShowPillScrollLeft(scrollLeftShown);
    setShowPillScrollRight(scrollRightShown);
  };

  const handlePillScroll = (direction: DIRECTION): void => {
    const pillsContainer = pillsContainerRef.current;

    if (!pillsContainer) {
      return;
    }

    pillsContainer.scrollBy({
      left: direction * PILL_SCROLL_WIDTH,
      behavior: 'smooth',
    });
  };

  const pillSelectHandler = (pill: ColorPalettePill): void => {
    const newSelectedPills = [...selectedPills, pill];
    setSelectedPills(newSelectedPills);
    setSearchTerm('');
    onSelectedPillsChange(newSelectedPills, pill);
  };

  const pillUnselectHandler = (pill: ColorPalettePill): void => {
    const newSelectedPills = selectedPills.filter((selectedPill) => {
      return selectedPill.id !== pill.id;
    });
    setSelectedPills(newSelectedPills);
    setSearchTerm('');
    onSelectedPillsChange(newSelectedPills);
  };

  const shouldShowSearchPills = (): boolean => {
    return colorPalettePillsLoaded && (searchTerm === '' || !showSearchResults);
  };

  const getCustomTextContentForPill = (pill: ColorPalettePill): ReactElement<CustomPillTextContentType, CustomPillTextContentProps> | undefined => {
    if (!pill.season) {
      return undefined;
    }

    let emojiText: string;
    let colorType: ColorType;

    switch (pill.season) {
      case SeasonName.HALLOWEEN:
        emojiText = SeasonalDecorationEmoji.HALLOWEEN;
        colorType = ColorType.HALLOWEEN;
        break;
      case SeasonName.CHRISTMAS:
        emojiText = SeasonalDecorationEmoji.CHRISTMAS;
        colorType = ColorType.CHRISTMAS;
        break;
      default:
        emojiText = '';
        colorType = ColorType.DEFAULT;
        break;
    }

    return <WavyText emojiText={emojiText} containerClassName={styles.pillText} colorType={colorType} val={pill.name} size={TextSize.XSMALL} bold />;
  };

  const getSearchPills = (): ReactElement<any> | null => {
    if (!shouldShowSearchPills()) {
      return null;
    }

    const pillsCount = colorPalettePills.length;
    const pills = [];

    for (let i = 0; i < pillsCount; i++) {
      pills.push(
        <PillButton
          key={getUniqueString()}
          className={styles.pill}
          text={colorPalettePills[i].name}
          isSelected={selectedPills.some((pill) => {
            return pill.id === colorPalettePills[i].id;
          })}
          size={PillButtonSize.SMALL}
          onClick={() => {
            pillSelectHandler(colorPalettePills[i]);
          }}
          onClose={() => {
            pillUnselectHandler(colorPalettePills[i]);
          }}
          customContent={getCustomTextContentForPill(colorPalettePills[i])}
        />
      );
    }

    pills.push(
      <div key={getUniqueString()} className={`${styles.pillScrollContainer} ${styles.scrollLeft} ${showPillScrollLeft ? '' : '_hidden'}`}>
        <Pill
          key={getUniqueString()}
          className={`${styles.pill} ${styles.pillScroll}`}
          content={{
            type: PILL_CONTENT_TYPE.ICON,
            icon: 'icon-caret-left',
            size: IconSize.SIZE_ICON_16,
          }}
          size={PILL_SIZE.MEDIUM}
          onClick={() => {
            handlePillScroll(DIRECTION.RIGHT);
          }}
        />
      </div>
    );

    pills.push(
      <div key={getUniqueString()} className={`${styles.pillScrollContainer} ${styles.scrollRight} ${showPillScrollRight ? '' : '_hidden'}`}>
        <Pill
          className={`${styles.pill} ${styles.pillScroll}`}
          content={{
            type: PILL_CONTENT_TYPE.ICON,
            icon: 'icon-caret-right',
            size: IconSize.SIZE_ICON_16,
          }}
          size={PILL_SIZE.MEDIUM}
          onClick={() => {
            handlePillScroll(DIRECTION.LEFT);
          }}
        />
      </div>
    );

    return (
      <div ref={pillsContainerRef} className={styles.pillsContainer}>
        {pills}
      </div>
    );
  };

  const searchInputSubmitHandler = (): void => {
    if (!searchTerm) {
      return;
    }

    const colorPalettesToSearch = [...(getAdminColorPalettesFromRedux() ?? []), ...brandKitPalettes];
    const searchedColorPalettes = filterColorPalettesBySearchTerm(searchTerm, colorPalettesToSearch);

    onSearchSubmit(searchTerm, searchedColorPalettes);
    setSelectedPills([]);
  };

  const searchInputChangeHandler = (newSearchTerm: string): void => {
    setSearchTerm(newSearchTerm);
    onSearchInput(newSearchTerm);
    if (newSearchTerm !== '') {
      setSelectedPills([]);
    }
  };

  useEffect(() => {
    void getColorPaletteFilterPills().then((colorPalettePills) => {
      setColorPalettePills(colorPalettePills);
      setColorPalettePillsLoaded(true);
    });
  }, []);

  useEffect(() => {
    showPillScrollIfNeeded();
  }, [selectedPills, colorPalettePillsLoaded]);

  useDidUpdateEffect(() => {
    const pillsContainer = pillsContainerRef.current;
    if (pillsContainer) {
      const mutationCallback = (): void => {
        showPillScrollIfNeeded();
        observer.disconnect();
      };

      const observer = new MutationObserver(mutationCallback);

      observer.observe(pillsContainer, {
        childList: true,
      });

      showPillScrollIfNeeded();

      pillsContainer.addEventListener('scroll', showPillScrollIfNeeded, {passive: true});
    }

    return (): void => {
      pillsContainer?.removeEventListener('scroll', showPillScrollIfNeeded);
    };
  }, [pillsContainerRef.current]);

  return (
    <div className={`${styles.searchContainer} ${showShadow ? styles.shadow : ''}`}>
      <InputField
        value={showSearchResults ? searchTerm : ''}
        onInputFocus={() => {
          setIsInputActive(true);
        }}
        onInputBlur={() => {
          setIsInputActive(false);
        }}
        className={`${styles.inputField} ${isInputActive ? styles.active : ''}`}
        placeholder={window.i18next.t('pmwjs_search_styles')}
        inputClassName={`body-md-regular ${styles.placeholder}`}
        inputIconSize={IconSize.SIZE_ICON_16}
        aria-label={window.i18next.t('pmwjs_search')}
        inputIconExtraClass="spacing-m-1"
        inputIconName={getSearchFieldIcon()}
        onInputSubmit={searchInputSubmitHandler}
        onInputChange={searchInputChangeHandler}
      />
      {getSearchPills()}
    </div>
  );
}
